
import { Options, prop, Vue } from "vue-class-component";
import GoodsPackageV4 from "@/app/ui/views/shipment/booking/create-booking-v4/components/goods-package/index.vue";
import {
  CommodityBookingV4,
  CommodityCategoryV4,
  ErrorBookingV4,
  PieceBookingV4
} from "@/domain/entities/BookingV4";
import { BookingV4Controller } from "@/app/ui/controllers/BookingV4Controller";
import { CommodityController } from "@/app/ui/controllers/CommodityController";
import { RequestListCommodityV4 } from "@/data/payload/api/BookingV4Request";
import { Commodity, CommodityCategoryData } from "@/domain/entities/Commodity";
import { volumeWeightForProduct } from "@/app/ui/views/shipment/booking/booking-utils";
import { ProductController } from "@/app/ui/controllers/ProductController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { configPrice } from "@/app/infrastructures/misc/common-library/modules/Price";
import { getCurrencyByCountryCode } from "@/app/infrastructures/misc/common-library/MalaysiaDistrictFieldRules";

const NUMERIC_LOCALE_BY_CURRENCY: Record<string, "id" | "my"> = {
  IDR: "id",
  MYR: "my"
};

interface SavedPackageDraft {
  id?: number;
  commodityCode: string;
  commodityName: string;
  isDangerousGoods: boolean;
  isQuarantine: boolean;
  pieces: Array<{
    grossWeight: number;
    length: number;
    width: number;
    height: number;
  }>;
}

class Props {
  index = prop<number>({ default: 1, type: Number });
  modelValue = prop<SavedPackageDraft>({ required: true });
}

@Options({
  name: "SavedPackageForm",
  components: {
    GoodsPackageV4
  },
  emits: ["update:modelValue", "validationChange"],
  watch: {
    "modelValue.pieces": {
      deep: true,
      handler() {
        const next = this.modelValue.pieces.map((p: {
          grossWeight: number;
          length: number;
          width: number;
          height: number;
        }) => this.toPiece(p));
        if (next.length !== this.piecesData.length) {
          this.piecesData = next;
        }
      }
    }
  }
})
export default class SavedPackageForm extends Vue.with(Props) {
  errors = new ErrorBookingV4();

  commoditySearchResults: CommodityBookingV4[] = [];
  commodityRecommendations: CommodityBookingV4[] = [];
  commodityListLoading = false;
  commodityListPage = 1;
  commodityListTotalData = 0;

  modalSearchResults: CommodityBookingV4[] = [];
  commodityModalLoading = false;
  commodityModalPage = 1;
  commodityModalTotalData = 0;

  commodityCategories: CommodityCategoryV4[] = [];
  commodityCategoryItems: CommodityBookingV4[] = [];
  selectedCategoryCode: string | null = null;
  commodityModalLoadingCategory = false;

  isCommoditySectionValid = false;
  isPackageWeightSectionValid = false;

  piecesData: PieceBookingV4[] = [];

  get isForeign(): boolean {
    return AccountController.accountData.accountIsForeign;
  }

  get accountCountryCode(): string {
    return (
      AccountController.accountData.countryCode ||
      AccountController.accountData.account_type_detail?.countryCode ||
      "id"
    );
  }

  get formAccountCurrencyCode(): string {
    return getCurrencyByCountryCode(this.accountCountryCode);
  }

  get numericLocale(): "id" | "my" {
    return NUMERIC_LOCALE_BY_CURRENCY[this.formAccountCurrencyCode] ?? "id";
  }

  get currencyPrefix(): string {
    return configPrice[this.formAccountCurrencyCode]?.prefix ?? "Rp";
  }

  get volumeWeightDivisor(): number {
    return volumeWeightForProduct(
      "",
      ProductController.volumetricDivisorMap,
      this.accountCountryCode
    );
  }

  get commodity(): CommodityBookingV4 | null {
    if (!this.modelValue.commodityCode) return null;
    return (
      this.commoditySearchResults.find(
        (c) => c.code === this.modelValue.commodityCode
      ) ||
      this.commodityRecommendations.find(
        (c) => c.code === this.modelValue.commodityCode
      ) ||
      new CommodityBookingV4({ code: this.modelValue.commodityCode })
    );
  }

  beforeMount() {
    this.piecesData = this.modelValue.pieces.map((p) => this.toPiece(p));
  }

  mounted() {
    this.refreshCommodityRecommendations();
    this.fetchCommodityCategories();
    this.searchCommodity("");
  }

  mapCommodityListToBookingV4(list: Commodity[]): CommodityBookingV4[] {
    return list.map(
      (item) =>
        new CommodityBookingV4({
          id: item.commodity_id,
          name: item.commodity_name,
          nameEn: item.commodity_name_en,
          code: item.commodity_code,
          isDangerousGoods: item.isDangerousGoods,
          isQuarantine: item.isQuarantine,
          status: item.commodity_status
        })
    );
  }

  async refreshCommodityRecommendations(): Promise<void> {
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          isRecommendedOnly: true,
          limit: 6,
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.commodityRecommendations = this.mapCommodityListToBookingV4(
        resp.data
      ).slice(0, 6);
    } catch (error) {
      this.commodityRecommendations = [];
    }
  }

  async fetchCommodityCategories(): Promise<void> {
    try {
      const categories = await CommodityController.getCommodityCategory(
        this.accountCountryCode.toUpperCase()
      );
      this.commodityCategories = categories.map(
        (item: CommodityCategoryData) =>
          ({
            code: item.commodityCategoryCode,
            name: item.commodityCategoryNameId,
            nameEn: item.commodityCategoryNameEn
          } as CommodityCategoryV4)
      );
    } catch (error) {
      this.commodityCategories = [];
    }
  }

  get commodityListHasMore(): boolean {
    return this.commodityListPage * 20 < this.commodityListTotalData;
  }

  async searchCommodity(keyword: string): Promise<void> {
    this.commodityListLoading = true;
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: 1,
          limit: 20,
          commodityStatus: "active",
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.commoditySearchResults = this.mapCommodityListToBookingV4(resp.data);
      this.commodityListPage = 1;
      this.commodityListTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      this.commoditySearchResults = [];
      this.commodityListTotalData = 0;
    } finally {
      this.commodityListLoading = false;
    }
  }

  async loadMoreCommodities(keyword: string): Promise<void> {
    if (!this.commodityListHasMore || this.commodityListLoading) return;
    this.commodityListLoading = true;
    try {
      const nextPage = this.commodityListPage + 1;
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: nextPage,
          limit: 20,
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.commoditySearchResults = [
        ...this.commoditySearchResults,
        ...this.mapCommodityListToBookingV4(resp.data)
      ];
      this.commodityListPage = nextPage;
      this.commodityListTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      // Keep the already-loaded page(s); just stop paginating on failure.
    } finally {
      this.commodityListLoading = false;
    }
  }

  get commodityModalHasMore(): boolean {
    return this.commodityModalPage * 20 < this.commodityModalTotalData;
  }

  async searchCommodityModal(keyword: string): Promise<void> {
    this.commodityModalLoading = true;
    this.selectedCategoryCode = null;
    this.commodityCategoryItems = [];
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: 1,
          limit: 20,
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.modalSearchResults = this.mapCommodityListToBookingV4(resp.data);
      this.commodityModalPage = 1;
      this.commodityModalTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      this.modalSearchResults = [];
      this.commodityModalTotalData = 0;
    } finally {
      this.commodityModalLoading = false;
    }
  }

  async loadMoreCommodityModal(keyword: string): Promise<void> {
    if (!this.commodityModalHasMore || this.commodityModalLoading) return;
    this.commodityModalLoading = true;
    try {
      const nextPage = this.commodityModalPage + 1;
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          search: keyword,
          page: nextPage,
          limit: 20,
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.modalSearchResults = [
        ...this.modalSearchResults,
        ...this.mapCommodityListToBookingV4(resp.data)
      ];
      this.commodityModalPage = nextPage;
      this.commodityModalTotalData = resp.pagination.totalData || 0;
    } catch (error) {
      // Keep the already-loaded page(s); just stop paginating on failure.
    } finally {
      this.commodityModalLoading = false;
    }
  }

  async selectCommodityCategory(code: string): Promise<void> {
    this.selectedCategoryCode = code;
    this.commodityModalLoadingCategory = true;
    try {
      const resp = await BookingV4Controller.getCommodityList(
        new RequestListCommodityV4({
          commodityCategoryCode: code,
          page: 1,
          limit: 100,
          countryCode: this.accountCountryCode.toUpperCase()
        })
      );
      this.commodityCategoryItems = this.mapCommodityListToBookingV4(resp.data);
    } catch (error) {
      this.commodityCategoryItems = [];
    } finally {
      this.commodityModalLoadingCategory = false;
    }
  }

  onCommodityUpdate(value: CommodityBookingV4 | null): void {
    const name =
      this.isForeign && value?.nameEn
        ? this.toSentenceCase(value.nameEn)
        : value?.name
        ? this.toSentenceCase(value.name)
        : "";
    this.$emit("update:modelValue", {
      ...this.modelValue,
      commodityCode: value?.code ?? "",
      commodityName: name,
      isDangerousGoods: value?.isDangerousGoods ?? false,
      isQuarantine: value?.isQuarantine ?? false
    });
  }

  toSentenceCase(val: string): string {
    if (!val) return "";
    const isAllUpper = val === val.toUpperCase() && val !== val.toLowerCase();
    if (!isAllUpper) return val;
    return val
      .toLowerCase()
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(" ");
  }

  onReset() {
    this.$emit("update:modelValue", {
      ...this.modelValue,
      commodityCode: "",
      commodityName: "",
      isDangerousGoods: false,
      isQuarantine: false,
      pieces: [{ grossWeight: 0, length: 3, width: 20, height: 10 }]
    });
  }

  toPiece(p: {
    grossWeight: number;
    length: number;
    width: number;
    height: number;
  }): PieceBookingV4 {
    return new PieceBookingV4({
      grossWeight: p.grossWeight,
      length: p.length,
      width: p.width,
      height: p.height,
      volumeWeight: this.computeVolumeWeight(p.length, p.width, p.height)
    });
  }

  computeVolumeWeight(length: number, width: number, height: number): number {
    if (!this.volumeWeightDivisor) return 0;
    return (length * width * height) / this.volumeWeightDivisor;
  }

  onPiecesUpdate(value: PieceBookingV4[]): void {
    this.piecesData = value;
    this.$emit("update:modelValue", {
      ...this.modelValue,
      pieces: value.map((p) => ({
        grossWeight: Number(p.grossWeight) || 0,
        length: Number(p.length) || 0,
        width: Number(p.width) || 0,
        height: Number(p.height) || 0
      }))
    });
  }

  onCommodityError(errorMessage: string): void {
    this.errors.commodity = errorMessage;
    this.emitValidationChange();
  }

  onCommodityValidationChange(isValid: boolean): void {
    this.isCommoditySectionValid = isValid;
    this.emitValidationChange();
  }

  onPackageWeightValidationChange(isValid: boolean): void {
    this.isPackageWeightSectionValid = isValid;
    this.emitValidationChange();
  }

  emitValidationChange(): void {
    this.$emit(
      "validationChange",
      this.isCommoditySectionValid && this.isPackageWeightSectionValid
    );
  }
}
